import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Pagination from '../../../components/common/Pagination';
import { listAuditLogsAidm } from '../../../api/aidmAuditApi';
import {
  AUDIT_USER_TYPES,
  getAuditActionGroup,
  getAuditTargetTypeLabel,
  getAuditUserTypeLabel,
} from '../../../utils/auditPresentation';

const USER_TYPES = AUDIT_USER_TYPES;

const PER_PAGE = 20;

const SERVICE_TABS = [
  { value: 'all', label: '전체 감사' },
  { value: 'ieum', label: '이음 운영' },
  { value: 'realtorauto', label: '부동산 오토' },
];

const ACTION_GROUPS = [
  { value: 'all', label: '전체 작업' },
  { value: 'auth', label: '로그인/인증' },
  { value: 'write', label: '생성/수정/삭제' },
  { value: 'consent', label: '동의/약관' },
  { value: 'payment', label: '결제/정산' },
  { value: 'webview', label: '앱/WebView' },
  { value: 'other', label: '기타' },
];

const formatDateTime = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const textOf = (v) => String(v || '').toLowerCase();
const safeJson = (value) => {
  if (!value || typeof value !== 'object') return '';
  try { return JSON.stringify(value); } catch { return ''; }
};
const prettyJson = (value) => {
  if (!value) return '(저장된 메타 데이터 없음)';
  if (typeof value !== 'object') return String(value);
  try { return JSON.stringify(value, null, 2); } catch { return '(메타 데이터를 표시할 수 없습니다)'; }
};
const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const getTargetKey = (log) => log.targetType || log.target || log.collection || log.table || '-';
const getTarget = (log) => log.targetLabel || getAuditTargetTypeLabel(getTargetKey(log));

const SystemLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [service, setService] = useState('all');
  const [userType, setUserType] = useState('all');
  const [actionGroup, setActionGroup] = useState('all');
  const [targetType, setTargetType] = useState('all');
  const [detail, setDetail] = useState(null);
  const [page, setPage] = useState(1);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      setLogs(await listAuditLogsAidm({ limit: 500, days: 30 }));
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const targetOptions = useMemo(() => {
    const labels = new Map();
    logs.forEach((log) => {
      const value = getTargetKey(log);
      if (value && value !== '-') labels.set(value, getTarget(log));
    });
    return [
      { value: 'all', label: '대상 전체' },
      ...Array.from(labels.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ko')),
    ];
  }, [logs]);

  const filtered = useMemo(() => logs.filter((log) => {
    if (service !== 'all' && log.service !== service) return false;
    if (userType !== 'all' && log.userType !== userType) return false;
    if (actionGroup !== 'all' && (log.actionGroup || getAuditActionGroup(log.action)) !== actionGroup) return false;
    if (targetType !== 'all' && getTargetKey(log) !== targetType) return false;
    if (search) {
      const q = textOf(search);
      const haystack = [
        log.action,
        log.actionLabel,
        log.user,
        log.userType,
        log.userTypeLabel,
        log.rawUserType,
        getTarget(log),
        getTargetKey(log),
        log.targetId,
        log.networkLocation,
        log.networkLocationDetail,
        log.ipLast4,
        log.accessEnvironment,
        safeJson(log.meta),
      ].map(textOf).join(' ');
      if (!haystack.includes(q)) return false;
    }
    return true;
  }), [logs, service, userType, actionGroup, targetType, search]);

  const serviceCounts = useMemo(() => ({
    all: logs.length,
    ieum: logs.filter((log) => log.service === 'ieum').length,
    realtorauto: logs.filter((log) => log.service === 'realtorauto').length,
  }), [logs]);

  const summary = useMemo(() => ({
    total: filtered.length,
    auth: filtered.filter((log) => (log.actionGroup || getAuditActionGroup(log.action)) === 'auth').length,
    writes: filtered.filter((log) => (log.actionGroup || getAuditActionGroup(log.action)) === 'write').length,
    app: filtered.filter((log) => (log.actionGroup || getAuditActionGroup(log.action)) === 'webview').length,
  }), [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const download = () => {
    const head = ['서비스', '일시', '사용자', '사용자 유형', '작업 내용', '대상', '대상 ID', '주소지', 'IP 끝 4자리', '접속환경'];
    const body = filtered.map((l) => [
      l.serviceLabel || l.service || '-',
      formatDateTime(l.date),
      l.user,
      l.userTypeLabel || getAuditUserTypeLabel(l.userType),
      l.actionLabel || l.action,
      getTarget(l),
      l.targetId || '',
      l.networkLocationDetail || l.networkLocation,
      l.ipLast4,
      l.accessEnvironment,
    ]);
    const csv = [head, ...body].map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `감사로그_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const userTypeLabel = (log) => log.userTypeLabel || getAuditUserTypeLabel(log.userType);

  return (
    <Container>
      <Header>
        <Title>감사 로그</Title>
        <HeaderActions>
          <TextBtn onClick={download}>현재 목록 내보내기(CSV)</TextBtn>
          <TextBtn onClick={fetchLogs}>새로고침</TextBtn>
        </HeaderActions>
      </Header>

      <ServiceTabs role="tablist" aria-label="감사 서비스 구분">
        {SERVICE_TABS.map((tab) => (
          <ServiceTab
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={service === tab.value}
            $active={service === tab.value}
            onClick={() => { setService(tab.value); setPage(1); }}
          >
            <span>{tab.label}</span>
            <TabCount>{(serviceCounts[tab.value] || 0).toLocaleString()}</TabCount>
          </ServiceTab>
        ))}
      </ServiceTabs>

      <SummaryRow>
        <SummaryCard>
          <SummaryLabel>현재 목록</SummaryLabel>
          <SummaryValue>{summary.total.toLocaleString()}건</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>로그인/인증</SummaryLabel>
          <SummaryValue>{summary.auth.toLocaleString()}건</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>생성/수정/삭제</SummaryLabel>
          <SummaryValue>{summary.writes.toLocaleString()}건</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>앱/WebView</SummaryLabel>
          <SummaryValue>{summary.app.toLocaleString()}건</SummaryValue>
        </SummaryCard>
      </SummaryRow>

      <FilterRow>
        <SearchInput
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="작업, 사용자, 대상, 주소지, IP 끝자리, 접속환경 검색"
        />
        <Select value={userType} onChange={(e) => { setUserType(e.target.value); setPage(1); }}>
          {USER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        <Select value={actionGroup} onChange={(e) => { setActionGroup(e.target.value); setPage(1); }}>
          {ACTION_GROUPS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        <Select value={targetType} onChange={(e) => { setTargetType(e.target.value); setPage(1); }}>
          {targetOptions.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>
      </FilterRow>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 160 }}>일시</Th>
              <Th style={{ width: 120 }}>서비스</Th>
              <Th style={{ width: 140 }}>사용자</Th>
              <Th style={{ width: 120 }}>사용자 유형</Th>
              <Th>작업 내용</Th>
              <Th style={{ width: 160 }}>대상</Th>
              <Th style={{ width: 160 }}>대상 ID</Th>
              <Th style={{ width: 190 }}>주소지</Th>
              <Th style={{ width: 110 }}>IP 끝 4자리</Th>
              <Th style={{ width: 210 }}>접속환경</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan={10} style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</Td></tr>
            ) : paged.length === 0 ? (
              <tr><Td colSpan={10} style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>로그가 없습니다.</Td></tr>
            ) : paged.map((log) => (
              <LogRow key={log.id} onClick={() => setDetail(log)}>
                <Td style={{ color: '#6B7280' }}>{formatDateTime(log.date)}</Td>
                <Td><ServiceBadge $service={log.service}>{log.serviceLabel || '-'}</ServiceBadge></Td>
                <Td>{log.user}</Td>
                <Td><UserTypeBadge $type={log.userType}>{userTypeLabel(log)}</UserTypeBadge></Td>
                <Td>{log.actionLabel || '기타 시스템 작업'}</Td>
                <Td style={{ color: '#6B7280' }}>{getTarget(log)}</Td>
                <Td><Mono>{log.targetId || '-'}</Mono></Td>
                <Td><LocationText title={log.networkLocationDetail || log.networkLocation}>{log.networkLocation}</LocationText></Td>
                <Td><Mono>{log.ipLast4}</Mono></Td>
                <Td><EnvironmentText>{log.accessEnvironment}</EnvironmentText></Td>
              </LogRow>
            ))}
          </tbody>
        </Table>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </TableWrap>

      {detail && (
        <Backdrop onClick={() => setDetail(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <div>
                <ModalTitle>{detail.actionLabel || '감사 로그 상세'}</ModalTitle>
                <ModalMeta>{formatDateTime(detail.date)} · {userTypeLabel(detail)}</ModalMeta>
              </div>
              <CloseBtn onClick={() => setDetail(null)}>닫기</CloseBtn>
            </ModalHead>
            <DetailGrid>
              <DetailItem><DetailLabel>서비스</DetailLabel><DetailValue>{detail.serviceLabel || '-'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>감사 범위</DetailLabel><DetailValue><Mono>{detail.scopeId || '-'}</Mono></DetailValue></DetailItem>
              <DetailItem><DetailLabel>사용자</DetailLabel><DetailValue>{detail.user || '-'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>사용자 유형</DetailLabel><DetailValue>{userTypeLabel(detail)}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>작업 내용</DetailLabel><DetailValue>{detail.actionLabel || '기타 시스템 작업'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>대상</DetailLabel><DetailValue>{getTarget(detail)}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>대상 ID</DetailLabel><DetailValue><Mono>{detail.targetId || '-'}</Mono></DetailValue></DetailItem>
              <DetailItem><DetailLabel>주소지</DetailLabel><DetailValue>{detail.networkLocationDetail || detail.networkLocation || '-'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>IP 끝 4자리</DetailLabel><DetailValue><Mono>{detail.ipLast4 || '-'}</Mono></DetailValue></DetailItem>
              <DetailItem><DetailLabel>접속환경</DetailLabel><DetailValue>{detail.accessEnvironment || '-'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>원본 작업 코드</DetailLabel><DetailValue><Mono>{detail.action || '-'}</Mono></DetailValue></DetailItem>
              <DetailItem><DetailLabel>원본 역할 코드</DetailLabel><DetailValue><Mono>{detail.rawUserType || '-'}</Mono></DetailValue></DetailItem>
            </DetailGrid>
            <MetaTitle>메타 데이터</MetaTitle>
            <MetaPre>{prettyJson(detail.meta)}</MetaPre>
          </Modal>
        </Backdrop>
      )}
      <GeoIpNotice>
        위치 허용 시 목록에 서울·경기·인천 같은 시·도를 표시하고 마우스오버로 한글 동·번지 상세주소를 확인합니다. 미허용 시 한글 시·도 근사값입니다.{' '}
        <a href="https://db-ip.com" target="_blank" rel="noreferrer">IP Geolocation by DB-IP</a>
      </GeoIpNotice>
    </Container>
  );
};

export default SystemLog;

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 16px;
`;

const TextBtn = styled.button`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
`;

const ServiceTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 2px;
  overflow-x: auto;
`;

const ServiceTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  background: ${({ $active }) => ($active ? '#EFF6FF' : '#fff')};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  white-space: nowrap;
`;

const TabCount = styled.span`
  min-width: 24px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(49, 130, 246, 0.1);
  font-size: 11px;
  text-align: center;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const SummaryCard = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 14px 16px;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  min-width: 220px;
  background: #fff;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: #fff;
  min-width: 160px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const LogRow = styled.tr`
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const ServiceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${({ $service }) => ($service === 'ieum' ? '#ECFDF5' : '#EFF6FF')};
  color: ${({ $service }) => ($service === 'ieum' ? '#047857' : '#1D4ED8')};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const UserTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${({ $type }) => {
    if ($type === 'admin') return '#FFF7ED';
    if ($type === 'realtor') return '#EFF6FF';
    if ($type === 'partner') return '#F5F3FF';
    if ($type === 'user') return '#ECFDF5';
    return '#F3F4F6';
  }};
  color: ${({ $type }) => {
    if ($type === 'admin') return '#C2410C';
    if ($type === 'realtor') return '#1D4ED8';
    if ($type === 'partner') return '#6D28D9';
    if ($type === 'user') return '#047857';
    return '#4B5563';
  }};
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;
`;

const Mono = styled.code`
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12px;
  color: #6B7280;
  word-break: break-all;
`;

const LocationText = styled.span`
  color: #374151;
  white-space: nowrap;
`;

const EnvironmentText = styled.span`
  color: #4B5563;
  white-space: nowrap;
`;

const GeoIpNotice = styled.p`
  margin: 10px 2px 0;
  color: #9CA3AF;
  font-size: 11px;
  text-align: right;
  a { color: #6B7280; text-decoration: underline; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1000;
`;

const Modal = styled.div`
  width: min(720px, 100%);
  max-height: 86vh;
  overflow: hidden;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.md};
  display: flex;
  flex-direction: column;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px;
`;

const ModalMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CloseBtn = styled.button`
  padding: 6px 12px;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const DetailItem = styled.div``;

const DetailLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const DetailValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
`;

const MetaTitle = styled.div`
  padding: 16px 20px 8px;
  font-size: 13px;
  font-weight: 600;
`;

const MetaPre = styled.pre`
  margin: 0 20px 20px;
  padding: 14px 16px;
  overflow: auto;
  background: #F8FAFC;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  white-space: pre-wrap;
`;
